import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  setDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { slugify, BLOG_STATUS } from '../utils/blogHelpers';

const POSTS_COLLECTION = 'blogPosts';
const CATEGORIES_COLLECTION = 'blogCategories';
const TAGS_COLLECTION = 'blogTags';

export const blogService = {
  // ---------- Posts (admin) ----------

  getAllPosts: async () => {
    try {
      const q = query(collection(db, POSTS_COLLECTION), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch (error) {
      console.error('Error fetching blog posts:', error);
      throw error;
    }
  },

  getPostById: async (id) => {
    try {
      const snap = await getDoc(doc(db, POSTS_COLLECTION, id));
      if (!snap.exists()) return null;
      return { id: snap.id, ...snap.data() };
    } catch (error) {
      console.error('Error fetching blog post:', error);
      throw error;
    }
  },

  // Checks the slug isn't already used by a different post.
  isSlugTaken: async (slug, excludeId = null) => {
    try {
      const q = query(collection(db, POSTS_COLLECTION), where('slug', '==', slug));
      const snapshot = await getDocs(q);
      return snapshot.docs.some((d) => d.id !== excludeId);
    } catch (error) {
      console.error('Error checking slug availability:', error);
      throw error;
    }
  },

  createPost: async (postData) => {
    try {
      const docRef = await addDoc(collection(db, POSTS_COLLECTION), {
        ...postData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      await blogService.registerTags(postData.tags);
      return { id: docRef.id, ...postData };
    } catch (error) {
      console.error('Error creating blog post:', error);
      throw error;
    }
  },

  updatePost: async (id, postData) => {
    try {
      await updateDoc(doc(db, POSTS_COLLECTION, id), {
        ...postData,
        updatedAt: serverTimestamp()
      });
      await blogService.registerTags(postData.tags);
      return { id, ...postData };
    } catch (error) {
      console.error('Error updating blog post:', error);
      throw error;
    }
  },

  deletePost: async (id) => {
    try {
      await deleteDoc(doc(db, POSTS_COLLECTION, id));
      return { success: true };
    } catch (error) {
      console.error('Error deleting blog post:', error);
      throw error;
    }
  },

  // ---------- Posts (public) ----------

  // Published posts only; filters out future-scheduled ones client-side (Firestore can't
  // compare against "now" server-side without an extra composite index per status).
  getPublishedPosts: async () => {
    try {
      const q = query(
        collection(db, POSTS_COLLECTION),
        where('status', 'in', [BLOG_STATUS.PUBLISHED, BLOG_STATUS.SCHEDULED]),
        orderBy('publishedAt', 'desc')
      );
      const snapshot = await getDocs(q);
      const now = new Date();
      return snapshot.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((post) => {
          const publishDate = post.publishedAt?.toDate ? post.publishedAt.toDate() : new Date(post.publishedAt);
          return publishDate && publishDate.getTime() <= now.getTime();
        });
    } catch (error) {
      console.error('Error fetching published posts:', error);
      throw error;
    }
  },

  getPublishedPostBySlug: async (slug) => {
    try {
      const q = query(collection(db, POSTS_COLLECTION), where('slug', '==', slug), limit(1));
      const snapshot = await getDocs(q);
      if (snapshot.empty) return null;
      const post = { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
      const now = new Date();
      const publishDate = post.publishedAt?.toDate ? post.publishedAt.toDate() : (post.publishedAt ? new Date(post.publishedAt) : null);
      const isVisible = (post.status === BLOG_STATUS.PUBLISHED || post.status === BLOG_STATUS.SCHEDULED)
        && publishDate && publishDate.getTime() <= now.getTime();
      return isVisible ? post : null;
    } catch (error) {
      // A draft post is rejected by Firestore security rules for non-admins — treat that as "not found" (404).
      if (error.code === 'permission-denied') return null;
      console.error('Error fetching blog post by slug:', error);
      throw error;
    }
  },

  // ---------- Categories ----------

  getCategories: async () => {
    try {
      const snapshot = await getDocs(collection(db, CATEGORIES_COLLECTION));
      return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch (error) {
      console.error('Error fetching blog categories:', error);
      throw error;
    }
  },

  createCategory: async (name) => {
    try {
      const slug = slugify(name);
      await setDoc(doc(db, CATEGORIES_COLLECTION, slug), { name, slug }, { merge: true });
      return { id: slug, name, slug };
    } catch (error) {
      console.error('Error creating blog category:', error);
      throw error;
    }
  },

  deleteCategory: async (id) => {
    try {
      await deleteDoc(doc(db, CATEGORIES_COLLECTION, id));
      return { success: true };
    } catch (error) {
      console.error('Error deleting blog category:', error);
      throw error;
    }
  },

  // ---------- Tags ----------

  getTags: async () => {
    try {
      const snapshot = await getDocs(collection(db, TAGS_COLLECTION));
      return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch (error) {
      console.error('Error fetching blog tags:', error);
      throw error;
    }
  },

  // Keeps the tags registry in sync whenever a post is saved with new tags (idempotent).
  registerTags: async (tags = []) => {
    if (!Array.isArray(tags) || tags.length === 0) return;
    try {
      await Promise.all(tags.map(async (tagName) => {
        const slug = slugify(tagName);
        if (!slug) return;
        await setDoc(doc(db, TAGS_COLLECTION, slug), { name: tagName, slug }, { merge: true });
      }));
    } catch (error) {
      console.error('Error registering blog tags:', error);
    }
  }
};
